import { useCallback, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '../../../hooks/context/useTheme'
import { useLocalization, LocalizationKeys } from '../../../i18n'
import { useSubMenuOptions } from '../../header/userProfile/userProfileDropdownContainer/hooks/useSubMenuOptions'
import { usePortalPopup } from '../../../hooks'
import { useToast } from '../../../hooks/context/useToast'
import { ToastType } from '../../../types/toast'
import styles from './themeDropdown.module.css'
import sharedStyles from '../settingsDropdown.module.css'

export interface ThemeDropdownProps {
  onClose: () => void
  buttonRef: RefObject<HTMLButtonElement | null>
}

const PORTAL_OPTIONS = { placement: 'top' as const, offset: 8 }

export function ThemeDropdown({ onClose, buttonRef }: ThemeDropdownProps) {
  const { t } = useLocalization()
  const { theme, setTheme } = useTheme()
  const { themeOptions } = useSubMenuOptions()
  const dropdownPosition = usePortalPopup(true, buttonRef, PORTAL_OPTIONS)
  const { addToast } = useToast()

  const handleThemeSelect = useCallback((selectedTheme: number) => {
    setTheme(selectedTheme)
    addToast(t(LocalizationKeys.MENU_THEME_CHANGED), ToastType.Warning)
    onClose()
  }, [setTheme, onClose, addToast, t])

  if (!dropdownPosition) {
    return null
  }

  return createPortal(
    <div 
      className={sharedStyles.dropdown} 
      role="menu"
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        transform: 'translateY(-100%)',
      }}
    >
      <div className={styles.header}>
        <span className={styles.title}>{t(LocalizationKeys.MENU_SELECT_THEME)}</span>
      </div>
      
      <nav>
        <ul className={styles.options} role="none">
          {themeOptions.map((option) => (
            <li key={option.value} className={styles.option} role="none">
              <button
                type="button"
                onClick={() => handleThemeSelect(option.value as number)}
                className={styles.optionBtn}
                data-active={theme === option.value}
                role="menuitem"
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>,
    document.body
  )
}
